import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { getDisplayName } from '../utils/displayHelpers';
import { 
  Calendar, 
  IndianRupee, 
  Star,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  Settings,
  BarChart3,
  MessageCircle,
  HomeIcon,
  Bell,
  ExternalLink,
  Wallet,
  User
} from 'lucide-react';
import { getCaregiverServices } from '../services/servicesService';
import DashboardSkeleton from '../components/ui/DashboardSkeleton';
import DataFetchState from '../components/ui/DataFetchState';

const CaregiverDashboard = () => {
  const { user, updateUser } = useAuth();
  const {
    offlineJobs,
    checkFeatureAccess,
    bookings,
    expenses,
    userDataLoading,
    userDataError,
    refreshUserData,
  } = useData();
  const [isAvailable, setIsAvailable] = useState(user?.isAvailable !== false); // Default to available
  
  const hasOfflineAccess = checkFeatureAccess(user?.id, 'offline_jobs');
  
  // Use uid as primary, fallback to id for consistency
  const userId = user?.uid || user?.id;

  const [hasNoServices, setHasNoServices] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    getCaregiverServices(userId).then((result) => {
      if (cancelled) return;
      if (result.success) {
        setHasNoServices(result.services.length === 0);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);
  
  const userOfflineJobs = offlineJobs.filter(job => job.userId === userId);

  // Filter bookings for this caregiver
  const caregiverBookings = useMemo(() => {
    return bookings.filter(b => b.caregiverId === userId);
  }, [bookings, userId]);

  // Calculate booking statistics
  const bookingStats = useMemo(() => {
    const pending = caregiverBookings.filter(b => 
      b.status === 'pending' || b.status === 'requested'
    );
    const active = caregiverBookings.filter(b => 
      b.status === 'accepted' || b.status === 'confirmed' || b.status === 'in_progress'
    );
    const upcoming = caregiverBookings.filter(b => {
      if (b.status !== 'accepted' && b.status !== 'confirmed') return false;
      const startDate = new Date(b.startDate || b.start);
      const today = new Date();
      return startDate >= today;
    }).sort((a, b) => new Date(a.startDate || a.start) - new Date(b.startDate || b.start));

    return {
      pendingCount: pending.length,
      activeCount: active.length,
      pendingRequests: pending.slice(0, 3), // Top 3 for display
      upcomingAppointments: upcoming.slice(0, 3) // Top 3 for display
    };
  }, [caregiverBookings]);

  // Calculate PnL (Profit and Loss) - offline + online - expenses
  const pnlData = useMemo(() => {
    console.log('🔍 PnL Debug Info:', {
      userId,
      offlineJobsCount: offlineJobs.length,
      userOfflineJobsCount: userOfflineJobs.length,
      expensesCount: expenses.length,
      bookingsCount: caregiverBookings.length
    });

    // Calculate platform (online) earnings from completed bookings
    const platformEarnings = caregiverBookings
      .filter(b => b.status === 'completed')
      .reduce((sum, b) => sum + (parseFloat(b.totalCost) || 0), 0);
    
    // Calculate offline earnings from external jobs - handle both amount fields
    const offlineEarnings = userOfflineJobs.reduce((sum, j) => {
      const jobAmount = parseFloat(j.amount) || 0;
      const taxAmount = parseFloat(j.taxAmount) || 0;
      console.log('💰 Offline Job:', j.id, 'Amount:', jobAmount, 'Tax:', taxAmount);
      return sum + jobAmount + taxAmount;
    }, 0);
    
    // Calculate total expenses for this caregiver - handle both user ID formats
    const totalExpenses = expenses
      .filter(e => e.userId === userId || e.userId === user?.id)
      .reduce((sum, e) => {
        const expenseAmount = parseFloat(e.amount) || 0;
        console.log('💸 Expense:', e.id, 'Amount:', expenseAmount, 'UserId:', e.userId);
        return sum + expenseAmount;
      }, 0);
    
    // Calculate net profit/loss
    const totalRevenue = platformEarnings + offlineEarnings;
    const netProfit = totalRevenue - totalExpenses;
    
    console.log('📊 PnL Calculation:', {
      platformEarnings,
      offlineEarnings,
      totalRevenue,
      totalExpenses,
      netProfit
    });
    
    return {
      platformEarnings,
      offlineEarnings,
      totalRevenue,
      totalExpenses,
      netProfit,
      profitMargin: totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(1) : 0,
      externalJobsCount: userOfflineJobs.length
    };
  }, [caregiverBookings, userOfflineJobs, expenses, userId, user?.id, offlineJobs.length]);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('bank');
  const [withdrawStatus, setWithdrawStatus] = useState(null);
  const [completedJobsFilter, setCompletedJobsFilter] = useState('all'); // all, platform, external

  // Handle availability toggle
  const handleAvailabilityToggle = () => {
    const newStatus = !isAvailable;
    setIsAvailable(newStatus);
    updateUser({ isAvailable: newStatus });
  };

  const stats = [
    {
      title: 'Active Bookings',
      value: bookingStats.activeCount,
      change: bookingStats.activeCount > 0 
        ? `${bookingStats.activeCount} booking${bookingStats.activeCount > 1 ? 's' : ''} in progress`
        : 'No active bookings',
      icon: Calendar,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Net Profit (PnL)',
      value: `₹${pnlData.netProfit.toLocaleString()}`,
      change: hasOfflineAccess 
        ? `Revenue: ₹${pnlData.totalRevenue.toLocaleString()} | Expenses: ₹${pnlData.totalExpenses.toLocaleString()}`
        : `Online: ₹${pnlData.platformEarnings.toLocaleString()} | Expenses: ₹${pnlData.totalExpenses.toLocaleString()}`,
      icon: pnlData.netProfit >= 0 ? TrendingUp : TrendingDown,
      color: pnlData.netProfit >= 0 ? 'from-green-500 to-emerald-500' : 'from-red-500 to-rose-500',
      isProfitLoss: true
    },
    {
      title: 'New Requests',
      value: bookingStats.pendingCount,
      change: bookingStats.pendingCount > 0 
        ? `${bookingStats.pendingCount} pending request${bookingStats.pendingCount > 1 ? 's' : ''}`
        : 'No new requests',
      icon: Bell,
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'Your Rating',
      value: user?.rating ? parseFloat(user.rating).toFixed(1) : '0.0',
      change: `${user?.totalReviews || 0} review${(user?.totalReviews || 0) !== 1 ? 's' : ''}`,
      icon: Star,
      color: 'from-yellow-500 to-orange-500'
    }
  ];

  const pendingRequests = bookingStats.pendingRequests;

  const upcomingAppointments = bookingStats.upcomingAppointments;

  const baseQuickActions = [
    {
      title: 'Booking requests',
      desc: 'Review and respond to owners',
      icon: Bell,
      link: '/caregiver-bookings',
      badge: bookingStats.pendingCount > 0 ? bookingStats.pendingCount : null,
    },
    {
      title: 'Messages',
      desc: 'Chat with pet owners',
      icon: MessageCircle,
      link: '/caregiver-messages',
    },
    {
      title: 'My services',
      desc: 'Update what you offer',
      icon: Settings,
      link: '/caregiver-services',
    },
    {
      title: 'Set availability',
      desc: 'Manage your schedule',
      icon: Calendar,
      link: '/caregiver-availability',
    },
    {
      title: 'Track expenses',
      desc: 'Business costs & P&L',
      icon: IndianRupee,
      link: '/caregiver-expenses',
      badge: pnlData.totalExpenses > 0 ? `₹${Math.round(pnlData.totalExpenses / 1000)}K` : null,
    },
    {
      title: 'Edit profile',
      desc: 'Your public caregiver page',
      icon: User,
      link: '/edit-caregiver-profile',
    },
  ];

  // Add external jobs if user has access
  const quickActions = hasOfflineAccess
    ? [
        ...baseQuickActions,
        {
          title: 'External jobs',
          desc: 'Offline client work',
          icon: ExternalLink,
          link: '/caregiver-offline-jobs',
          badge: pnlData.externalJobsCount > 0 ? `${pnlData.externalJobsCount}` : null,
        },
      ]
    : baseQuickActions;

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return;
    
    setWithdrawStatus('processing');
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setWithdrawStatus('success');
      setWithdrawAmount('');
      setTimeout(() => {
        setShowWithdrawModal(false);
        setWithdrawStatus(null);
      }, 2000);
    } catch (error) {
      setWithdrawStatus('error');
      setTimeout(() => setWithdrawStatus(null), 3000);
    }
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Welcome Banner */}
        <div className="pf-organic-card pf-organic-card--dark mb-4 md:mb-8 overflow-hidden relative p-4 md:p-8">
          <div className="pf-hero-v2__pattern absolute inset-0 opacity-30" />
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1596492784531-6e6eb5ea9993?auto=format&fit=crop&w=2000&q=80')] opacity-10" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 md:gap-6">
            <div className="flex-1">
              <span className="pf-eyebrow pf-eyebrow--on-dark mb-2">Caregiver dashboard</span>
              <h1 className="pf-display text-xl md:text-3xl lg:text-4xl text-white mb-1 md:mb-2">
                Welcome back, {getDisplayName(user)}!
              </h1>
              <p className="text-white/80 text-sm md:text-base mb-3 md:mb-4">
                Here&apos;s your caregiving dashboard overview
              </p>
              
              {/* Availability Status Toggle - Improved Mobile Layout */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 w-fit">
                  <span className={`text-xs md:text-sm font-semibold ${isAvailable ? 'text-white' : 'text-white/70'}`}>
                    {isAvailable ? 'Available for Bookings' : 'Not Available'}
                  </span>
                  
                  {/* Modern Toggle Switch - Smaller on Mobile */}
                  <button
                    onClick={handleAvailabilityToggle}
                    className={`relative inline-flex h-6 w-12 md:h-8 md:w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[var(--pf-ink)] ${
                      isAvailable ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                    role="switch"
                    aria-checked={isAvailable}
                    title="Toggle your availability"
                  >
                    <span
                      className={`inline-block h-4 w-4 md:h-6 md:w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                        isAvailable ? 'translate-x-7 md:translate-x-9' : 'translate-x-1'
                      }`}
                    >
                      {/* Animated checkmark/cross icon */}
                      {isAvailable ? (
                        <svg className="h-4 w-4 md:h-6 md:w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="h-4 w-4 md:h-6 md:w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </span>
                  </button>
                </div>
                
                <span className="text-white/80 text-xs md:text-sm font-medium">
                  {isAvailable ? (
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                      Pet owners can contact you
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      You won't receive new requests
                    </span>
                  )}
                </span>
              </div>
            </div>
            <Link 
              to="/caregiver-services" 
              className="bg-white text-[var(--pf-accent)] hover:bg-blue-50 inline-flex items-center justify-center px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-semibold transition-colors group whitespace-nowrap shrink-0"
            >
              Manage Services
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* Untagged caregivers never appear in service-filtered searches */}
        {hasNoServices && (
          <div className="mb-4 md:mb-8 rounded-lg md:rounded-xl border-2 border-amber-300 bg-amber-50 p-4 md:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <h2 className="font-semibold text-amber-900 text-sm md:text-base">
                  Add your services so pet owners can find you
                </h2>
                <p className="text-amber-800 text-xs md:text-sm mt-1">
                  Owners search by service (boarding, walking, grooming and more). Until you list at
                  least one service, you won't show up in those search results.
                </p>
              </div>
              <Link
                to="/caregiver-services"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
              >
                Add Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        )}

        {/* Stats Grid - More Compact on Mobile */}
        {userDataLoading ? (
          <DashboardSkeleton statCount={4} />
        ) : userDataError ? (
          <DataFetchState
            error={userDataError}
            onRetry={refreshUserData}
            errorTitle="Couldn't load your dashboard"
            compact
            className="mb-4 md:mb-8"
          />
        ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="pf-organic-card p-3 md:p-6 group">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-[var(--pf-accent-soft)] flex items-center justify-center transform transition-transform group-hover:scale-110">
                    <Icon className="w-5 h-5 md:w-6 md:h-6 text-[var(--pf-accent)]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs md:text-sm text-gray-600 truncate">{stat.title}</p>
                    <p className={`text-lg md:text-2xl font-bold ${
                      stat.isProfitLoss 
                        ? pnlData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'
                        : 'text-gray-900'
                    }`}>
                      {stat.value}
                    </p>
                    <p className={`text-[10px] md:text-xs font-medium mt-0.5 md:mt-1 line-clamp-1 ${
                      stat.change.includes('Revenue:') || stat.change.includes('Online:') ? 'text-gray-600' :
                      stat.change.includes('+') ? 'text-green-600' :
                      stat.change === 'New!' ? 'text-orange-600' :
                      'text-gray-600'
                    }`}>
                      {stat.change}
                    </p>
                    {stat.isProfitLoss && (
                      <p className="text-[10px] md:text-xs text-gray-500 mt-0.5">
                        {pnlData.profitMargin}% margin
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 md:space-y-8">
            {/* Pending Booking Requests - Compact on Mobile */}
            <div className="pf-organic-card border-amber-200 bg-amber-50/80 p-4 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-lg md:text-xl font-semibold text-[var(--pf-ink)]">Pending requests</h2>
                <Link 
                  to="/caregiver-bookings" 
                  className="text-[var(--pf-accent)] hover:opacity-80 text-sm font-medium inline-flex items-center group"
                >
                  View All
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="space-y-4">
                {pendingRequests.length > 0 ? (
                  pendingRequests.map((request) => (
                    <div key={request.id} className="p-4 bg-white rounded-xl shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[var(--pf-accent)] flex items-center justify-center text-white font-semibold">
                            {request.ownerName ? request.ownerName.charAt(0).toUpperCase() : 'U'}
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{request.ownerName || 'Pet Owner'}</h3>
                            <p className="text-sm text-gray-600">{request.petName || request.serviceName || 'Service'}</p>
                          </div>
                        </div>
                        <Link 
                          to="/caregiver-bookings"
                          className="bg-blue-500 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-600 transition-colors"
                        >
                          View Details
                        </Link>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {request.startDate ? new Date(request.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Date TBD'}
                        </div>
                        <div className="font-medium text-gray-900">₹{(request.totalCost || request.amount || 0).toLocaleString()}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No pending requests</p>
                    <p className="text-sm text-gray-400 mt-1">New booking requests will appear here</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming Appointments */}
            <div className="pf-organic-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[var(--pf-ink)]">Upcoming appointments</h2>
                <Link 
                  to="/caregiver-bookings" 
                  className="text-[var(--pf-accent)] hover:opacity-80 text-sm font-medium inline-flex items-center group"
                >
                  View All Bookings
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="space-y-4">
                {upcomingAppointments.length > 0 ? (
                  upcomingAppointments.map((appointment) => {
                    const startDate = new Date(appointment.startDate || appointment.start);
                    const today = new Date();
                    const isToday = startDate.toDateString() === today.toDateString();
                    
                    return (
                      <div 
                        key={appointment.id} 
                        className={`p-4 rounded-xl border-l-4 ${
                          isToday ? 'bg-blue-50 border-blue-500' : 'bg-gray-50 border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[var(--pf-accent)] flex items-center justify-center text-white font-semibold text-sm">
                              {appointment.ownerName ? appointment.ownerName.charAt(0).toUpperCase() : 'U'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{appointment.ownerName || 'Pet Owner'}</p>
                              <p className="text-sm text-gray-600">{appointment.petName || appointment.serviceName || 'Service'}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            isToday ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'
                          }`}>
                            {isToday ? 'Today' : startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {appointment.location && (
                            <div className="flex items-center gap-1">
                              <HomeIcon className="w-4 h-4" />
                              {appointment.location}
                            </div>
                          )}
                          {(appointment.endDate || appointment.end) && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {Math.ceil((new Date(appointment.endDate || appointment.end) - startDate) / (1000 * 60 * 60 * 24))} days
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No upcoming appointments</p>
                    <p className="text-sm text-gray-400 mt-1">Your confirmed bookings will show here</p>
                  </div>
                )}
              </div>
            </div>

            {/* Completed Jobs */}
            <div className="pf-organic-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[var(--pf-ink)]">Completed jobs</h2>
                <Link to="/caregiver-bookings" className="text-[var(--pf-accent)] hover:opacity-80 text-sm font-medium flex items-center gap-1">
                  View All
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 mb-4 border-b border-gray-200">
                {['all', 'platform', 'external'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setCompletedJobsFilter(filter)}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                      completedJobsFilter === filter
                        ? 'border-[var(--pf-accent)] text-[var(--pf-accent)]'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {filter === 'all' ? 'All Jobs' : filter === 'platform' ? 'Platform' : 'External'}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {/* Only show external jobs for fresh accounts - no mock data */}
                {userOfflineJobs.length > 0 ? (
                  userOfflineJobs.slice(0, 2).map(job => ({
                    id: job.id,
                    owner: { name: job.clientName, avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + job.clientName },
                    pet: { name: job.petName, breed: job.serviceType },
                    service: job.serviceType,
                    date: new Date(job.jobDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                    amount: job.amount + (job.taxAmount || 0),
                    source: 'external'
                  }))
                  .filter(job => completedJobsFilter === 'all' || job.source === completedJobsFilter)
                  .map((job) => (
                    <div key={job.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <img 
                            src={job.owner.avatar} 
                            alt={job.owner.name} 
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-medium text-gray-900">{job.owner.name}</p>
                            <p className="text-sm text-gray-600">{job.pet.name} - {job.service}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">₹{job.amount}</p>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            External
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        {job.date}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No completed jobs yet</p>
                    <p className="text-sm text-gray-400 mt-1">Start accepting bookings to build your work history</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* PnL Overview */}
            <div className="pf-organic-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Wallet className="w-5 h-5 text-[var(--pf-accent)]" />
                <h2 className="text-xl font-semibold text-[var(--pf-ink)]">Profit & Loss</h2>
              </div>
              <div className="space-y-3">
                {/* Revenue Section */}
                <div className="bg-[var(--pf-accent-soft)] rounded-lg p-3 border border-[var(--pf-border)]">
                  <p className="text-xs text-gray-500 mb-2 font-medium">REVENUE</p>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Online Bookings</span>
                    <span className="font-semibold text-[var(--pf-accent)]">+₹{pnlData.platformEarnings.toLocaleString()}</span>
                  </div>
                  {hasOfflineAccess && (
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600">Offline Jobs</span>
                      <span className="font-semibold text-[var(--pf-accent)]">+₹{pnlData.offlineEarnings.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-[var(--pf-border)] mt-2 pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Total Revenue</span>
                      <span className="font-bold text-[var(--pf-accent)]">₹{pnlData.totalRevenue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Expenses Section */}
                <div className="bg-[var(--pf-surface)] rounded-lg p-3 border border-red-200">
                  <p className="text-xs text-gray-500 mb-2 font-medium">EXPENSES</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Expenses</span>
                    <span className="font-semibold text-red-600">-₹{pnlData.totalExpenses.toLocaleString()}</span>
                  </div>
                  <Link 
                    to="/caregiver-expenses" 
                    className="text-xs text-[var(--pf-accent)] hover:opacity-80 mt-1 inline-block"
                  >
                    View breakdown →
                  </Link>
                </div>

                {/* Net Profit */}
                <div className={`rounded-lg p-4 border-2 ${
                  pnlData.netProfit >= 0 
                    ? 'bg-[var(--pf-mint)] border-[var(--pf-mint-deep)]' 
                    : 'bg-red-50 border-red-300'
                }`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Net Profit</p>
                      <p className="text-xs text-gray-600">{pnlData.profitMargin}% margin</p>
                    </div>
                    <p className={`text-2xl font-bold ${
                      pnlData.netProfit >= 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      ₹{pnlData.netProfit.toLocaleString()}
                    </p>
                  </div>
                </div>

                <button 
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={pnlData.totalRevenue <= 0}
                  className="pf-btn-primary w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Withdraw Earnings
                </button>
              </div>
            </div>

            <div className="pf-organic-card p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-[var(--pf-ink)] mb-3 md:mb-4">Quick actions</h2>
              <div className="space-y-2 md:space-y-3">
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Link 
                      key={action.title}
                      to={action.link} 
                      className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl border border-[var(--pf-border)] hover:border-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)] transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[var(--pf-accent)] flex items-center justify-center relative shrink-0">
                        <Icon className="w-4 h-4 text-white" />
                        {action.badge ? (
                          <span className="absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                            {action.badge}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm md:text-base font-semibold text-[var(--pf-ink)] group-hover:text-[var(--pf-accent)] truncate">{action.title}</p>
                        {action.desc ? (
                          <p className="text-xs md:text-sm text-[var(--pf-ink-muted)] truncate">{action.desc}</p>
                        ) : null}
                      </div>
                      <ArrowRight className="w-4 h-4 text-[var(--pf-ink-muted)] group-hover:text-[var(--pf-accent)] shrink-0" />
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Performance */}
            <div className="pf-organic-card p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-[var(--pf-ink)] mb-3 md:mb-4">Performance</h2>
              <div className="space-y-2 md:space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Response Rate</span>
                  <span className="font-semibold text-gray-400">N/A</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Acceptance Rate</span>
                  <span className="font-semibold text-gray-400">N/A</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Completion Rate</span>
                  <span className="font-semibold text-gray-400">N/A</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Withdraw Modal */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4">Withdraw Earnings</h3>
              
              {withdrawStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Withdrawal Successful!</h4>
                  <p className="text-gray-600">Your funds will be transferred within 1-2 business days.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Available Balance */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-700">Available Balance (Total Revenue)</p>
                    <p className="text-2xl font-bold text-green-800">₹{pnlData.totalRevenue.toLocaleString()}</p>
                    <p className="text-xs text-green-600 mt-1">Net Profit: ₹{pnlData.netProfit.toLocaleString()}</p>
                  </div>

                  {/* Withdrawal Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Withdrawal Method</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setWithdrawMethod('bank')}
                        className={`p-3 border rounded-lg text-sm font-medium transition-colors ${
                          withdrawMethod === 'bank' 
                            ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        Bank Transfer
                      </button>
                      <button
                        onClick={() => setWithdrawMethod('upi')}
                        className={`p-3 border rounded-lg text-sm font-medium transition-colors ${
                          withdrawMethod === 'upi' 
                            ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        UPI
                      </button>
                    </div>
                  </div>

                  {/* Withdrawal Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="Enter amount"
                      min="100"
                      max={pnlData.totalRevenue}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setWithdrawAmount('5000')}
                        className="text-xs bg-gray-100 px-2 py-1 rounded"
                      >
                        ₹5,000
                      </button>
                      <button
                        onClick={() => setWithdrawAmount('10000')}
                        className="text-xs bg-gray-100 px-2 py-1 rounded"
                      >
                        ₹10,000
                      </button>
                      <button
                        onClick={() => setWithdrawAmount(pnlData.totalRevenue.toString())}
                        className="text-xs bg-gray-100 px-2 py-1 rounded"
                      >
                        All
                      </button>
                    </div>
                  </div>

                  {/* Processing Fee */}
                  {withdrawAmount && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        Processing fee: ₹{Math.max(25, parseFloat(withdrawAmount || 0) * 0.02)} 
                        <br />
                        You'll receive: ₹{parseFloat(withdrawAmount || 0) - Math.max(25, parseFloat(withdrawAmount || 0) * 0.02)}
                      </p>
                    </div>
                  )}

                  {withdrawStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-800">
                        Withdrawal failed. Please try again or contact support.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {withdrawStatus !== 'success' && (
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleWithdraw}
                    disabled={!withdrawAmount || parseFloat(withdrawAmount) <= 0 || withdrawStatus === 'processing'}
                    className="pf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {withdrawStatus === 'processing' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </>
                    ) : (
                      'Withdraw'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowWithdrawModal(false);
                      setWithdrawAmount('');
                      setWithdrawStatus(null);
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 flex-1"
                    disabled={withdrawStatus === 'processing'}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaregiverDashboard;
